import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Button, Stack } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useState } from "react";

import { useTenantTranslation } from "hooks/formatters";
import { useSearchId } from "hooks/search";
import { useTenant } from "hooks/settings";
import { useIsMobile } from "hooks/useIsMobile";

import { useMaterials } from "contexts/search/SearchContextProvider";
import { useTenantData } from "contexts/tenant";

import { PlanId } from "components/common/boundary/PlanId";
import { EditableTableSkeleton } from "components/common/plan/EditableTableSkeleton";
import { LayerEditableApproveBody } from "./LayerEditableApproveBody";

import {
  ChildMixUpdate,
  generatedApi,
  MaterialRead,
  NameBasedPeriodMaterialSummary,
  PlannedResultsRead,
  SteelGrade,
  useGetPlanResultsQuery,
  useUpdatePlanResultsMutation,
} from "src/store/api/generatedApi";
import {
  selectSearchedSteelGradeIds,
  steelGradeSearchSlice,
} from "src/store/slices/steelGradeSearchSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";

import { DeployTable } from "components/common/plan/DeployTable";
import { EditableTableActions } from "components/common/plan/EditableTableActions";
import { filterPlanResultsToPeriod } from "components/common/plan/utils";
import { minimumPeriod } from "hooks/periodIndex";
import { useMiscParams, useProductionSchedule } from "hooks/search";
import { useMixCollapse } from "hooks/useMixCollapse";
import { useMixGrouping } from "hooks/useMixGrouping";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";

interface ContentProps {
  planResults: PlannedResultsRead;
  materials: MaterialRead[];
  setPlanId: (planId: PlanId) => void;
  toNextState: () => void;
  periodMaterialSummaries: NameBasedPeriodMaterialSummary[];
  steelGrades: SteelGrade[];
}

export const ApprovePlanContent = ({
  planResults,
  materials,
  setPlanId,
  toNextState,
  periodMaterialSummaries,
  steelGrades,
}: ContentProps) => {
  const tenantName = useTenant();
  const searchId = useSearchId();
  const { t } = useTenantTranslation();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();

  const period = minimumPeriod;
  const periodResults = React.useMemo(
    () => planResults.periods[period.toString()]?.mixes ?? [],
    [planResults, period]
  );

  const [doUpdatePlanResultsMutation] = useUpdatePlanResultsMutation();

  const [getResult] = generatedApi.useLazyGetResultQuery();

  const handleOnSubmit = useCallback(
    async (updates: ChildMixUpdate[], comment: string) => {
      const { data: updated_plan, error } = await doUpdatePlanResultsMutation({
        tenantName,
        planId: planResults.plan_id,
        planMixUpdates: {
          period_updates: [
            {
              period: period,
              updates,
              replacements: {},
            },
          ],
          comment: comment,
        },
      });
      if (error === undefined) {
        void dispatch(
          generatedApi.util.upsertQueryData(
            "getPlanResults",
            { planId: updated_plan.plan_id, tenantName, period },
            filterPlanResultsToPeriod(updated_plan, period)
          )
        );

        dispatch(
          steelGradeSearchSlice.actions.copy([
            planResults.plan_id as PlanId,
            period,
            "deploy",
            updated_plan.plan_id as PlanId,
            period,
          ])
        );

        if (searchId === null) {
          throw new Error(
            `Search id should not be null in context with plan id ${planResults.plan_id}`
          );
        }

        await getResult({ searchId, tenantName });
        setPlanId(updated_plan.plan_id as PlanId);
      }
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- getResult is an async helper with unstable identity; including it risks recreating the callback on every render
    [
      searchId,
      doUpdatePlanResultsMutation,
      tenantName,
      planResults,
      setPlanId,
      dispatch,
    ]
  );

  const [defaultStartAt] = useState(() => new Date());
  const startAt = planResults.start_at
    ? new Date(planResults.start_at)
    : defaultStartAt;
  const endAt = planResults.end_at
    ? new Date(planResults.end_at)
    : dayjs(startAt).add(1, "day").toDate();

  const inventoryByMaterialName = React.useMemo(
    () =>
      typeSafeObjectFromEntries(
        periodMaterialSummaries
          .filter((summary) => summary.period === period)
          .map((summary) => [
            summary.material,
            summary.initial_inventory + summary.obtainable_obtained,
          ])
      ),
    [periodMaterialSummaries, period]
  );

  const filteredSteelGrades = React.useMemo(() => {
    const steelGradeNames = new Set(
      periodResults.flatMap((result) =>
        result.production.map((item) => item.steel_grade_name)
      )
    );
    return steelGrades.filter((steelGrade) =>
      steelGradeNames.has(steelGrade.name)
    );
  }, [periodResults, steelGrades]);

  const tableMaterials = React.useMemo(() => {
    return materials
      .filter((material) => material.addition_location === "basket")
      .sort(
        (materialA, materialB) =>
          materialA.deployment_ordering - materialB.deployment_ordering
      )
      .map((material) => ({
        id: material.id,
        name: material.name,
        inventory: inventoryByMaterialName[material.name] ?? null,
      }));
  }, [materials, inventoryByMaterialName]);

  const driInventory = materials.some(
    (material) => material.addition_location === "hopper"
  )
    ? materials
        .filter((material) => material.addition_location === "hopper")
        .reduce(
          (total, material) =>
            total + (inventoryByMaterialName[material.name] ?? 0),
          0
        )
    : null;

  const selectedSteelGradeIds = useAppSelector((state) =>
    selectSearchedSteelGradeIds(state, planResults.plan_id, period, "deploy")
  );
  const timeString = `${dayjs(startAt).format("MMM D YYYY HH:mm")} - ${dayjs(
    endAt
  ).format("MMM D YYYY HH:mm")}`;

  const mixGrouping = useMixGrouping();
  const collapse = useMixCollapse(planResults.plan_id as PlanId, period);

  const { layer_edit_mode } = useTenantData();
  if (layer_edit_mode !== "none") {
    return (
      <LayerEditableApproveBody
        planResults={planResults}
        materials={materials}
        steelGrades={steelGrades}
        tableMaterials={tableMaterials}
        driInventory={driInventory}
        timeString={timeString}
        setPlanId={setPlanId}
        toNextState={toNextState}
        onSubmit={handleOnSubmit}
      />
    );
  }

  return (
    <Stack sx={{ gap: 1, maxHeight: "100%" }}>
      <Button
        sx={{ alignSelf: "end" }}
        onClick={toNextState}
        endIcon={<ArrowForwardIcon />}
      >
        {t("approveAndNext")}
      </Button>
      <Stack flex={1} sx={{ overflow: "auto" }}>
        <EditableTableActions
          timeString={timeString}
          view="deploy"
          planId={planResults.plan_id as PlanId}
          period={period}
          materials={materials}
          showDriConsumption={driInventory !== null}
          steelGrades={filteredSteelGrades}
          onSubmit={handleOnSubmit}
          results={periodResults}
        >
          {(onChange, setMixDescription, isSubmitting, hasEdited, rows) => (
            <DeployTable
              planId={planResults.plan_id as PlanId}
              period={period}
              disableEdit={isSubmitting}
              hasEdited={hasEdited}
              rows={rows}
              materials={tableMaterials}
              selectedSteelGradeIds={selectedSteelGradeIds}
              onChange={onChange}
              mixGrouping={mixGrouping}
              driInventory={driInventory}
              steelGradesWidth={isMobile ? 120 : 210}
              onSubmit={handleOnSubmit}
              setMixDescription={setMixDescription}
              collapse={collapse}
            />
          )}
        </EditableTableActions>
      </Stack>
    </Stack>
  );
};

interface Props {
  toNextState: () => void;
  planId: PlanId;
  setPlanId: (planId: PlanId) => void;
}

export const ApprovePlan = ({ toNextState, planId, setPlanId }: Props) => {
  const tenantName = useTenant();
  const [productionSchedule] = useProductionSchedule();
  const [miscParams] = useMiscParams();
  const materials = useMaterials();
  const planResults = useGetPlanResultsQuery({ tenantName, planId, period: 1 });

  if (
    productionSchedule.status === "success" &&
    miscParams.status === "success" &&
    planResults.currentData &&
    materials.status === "success"
  ) {
    return (
      <ApprovePlanContent
        planResults={planResults.currentData}
        materials={materials.data.byIndex}
        steelGrades={planResults.currentData.steel_grade_set.steel_grades}
        periodMaterialSummaries={
          planResults.currentData.summary.period_material_summary
        }
        setPlanId={setPlanId}
        toNextState={toNextState}
      />
    );
  } else {
    return <EditableTableSkeleton />;
  }
};
